import {isFeatureEnabled} from 'feature-flags';
import i18n from 'i18next';
import {Form, FormSubmitHandler, Label, showNotification} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useGetOnlineOfferSettingsQuery, usePutOnlineOfferSettingsMutation} from '@dms/api/settings';
import {featureFlags} from '@dms/feature-flags';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {RichContentInput} from '@chat/ui';

import {DisclaimerInput} from './components/DisclaimerInput';

const digitalDealSchema = z.object({
  disclaimer: z.string().optional(),
  buyingDisclaimer: z.string().optional(),
});

type DigitalDealFormData = z.infer<typeof digitalDealSchema>;

export function DigitalDeal() {
  const isBuyingOnlineOfferEnabled = isFeatureEnabled(featureFlags.BUYING_ONLINE_OFFER);
  const [putOnlineOfferSettings, {isLoading: isPutOnlineOfferSettingsLoading}] =
    usePutOnlineOfferSettingsMutation();
  const {data: onlineOfferSettingsData, isLoading: isGetOnlineOfferSettingsQueryLoading} =
    useGetOnlineOfferSettingsQuery();

  const handleSubmit: FormSubmitHandler<DigitalDealFormData> = async (
    values,
    _setErrors,
    reset
  ) => {
    await putOnlineOfferSettings({
      onlineOfferSettingsRequestBody: {
        disclaimer: values.disclaimer ?? '',
        buyingDisclaimer: isBuyingOnlineOfferEnabled
          ? (values.buyingDisclaimer ?? '')
          : (onlineOfferSettingsData?.buyingDisclaimer ?? ''),
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success();
        reset(undefined, {keepValues: true});
      })
      .catch(handleApiError);
  };

  return (
    <SettingsTemplate
      header={{title: i18n.t('page.settings.labels.digitalDeal')}}
      data-testid="digital-deal-settings"
      description={i18n.t('page.settings.digitalDeal.description')}
      isLoading={isGetOnlineOfferSettingsQueryLoading}
    >
      <SettingsSection>
        <VStack spacing={6}>
          <Form
            onSubmit={handleSubmit}
            experimentalZodSchema={digitalDealSchema}
            defaultValues={{
              disclaimer: onlineOfferSettingsData?.disclaimer ?? '',
              buyingDisclaimer: onlineOfferSettingsData?.buyingDisclaimer ?? '',
            }}
            shouldWatchForUnsavedChanges
          >
            {(control) => (
              <VStack spacing={4}>
                <VStack>
                  <Label tooltip={i18n.t('page.settings.digitalDeal.disclaimerTooltip')}>
                    {i18n.t('page.settings.digitalDeal.disclaimer')}
                  </Label>
                  <RichContentInput
                    name="disclaimer"
                    ContentEditableComponent={<DisclaimerInput />}
                  />
                </VStack>

                <Show when={isBuyingOnlineOfferEnabled}>
                  <VStack>
                    <Label tooltip={i18n.t('page.settings.digitalDeal.buyingDisclaimerTooltip')}>
                      {i18n.t('page.settings.digitalDeal.buyingDisclaimer')}
                    </Label>
                    <RichContentInput
                      name="buyingDisclaimer"
                      ContentEditableComponent={<DisclaimerInput />}
                    />
                  </VStack>
                </Show>

                <SettingsFooter
                  actions={[
                    {
                      type: 'form-button',
                      buttonType: 'submit',
                      title: i18n.t('general.actions.save'),
                      control,
                      isLoading: isPutOnlineOfferSettingsLoading,
                    },
                  ]}
                />
              </VStack>
            )}
          </Form>
        </VStack>
      </SettingsSection>
    </SettingsTemplate>
  );
}
